import React, { type FC, type PropsWithChildren, useContext, useState, useEffect } from 'react';

import { isBrowser } from '@lucid/utils';
import ReactModal from 'react-modal';
import { useTheme } from '@emotion/react';
import { useSmartlingStrings } from '@lucid/hooks';

import Box from '../box/box';
import Link from '../link/link';
import Icon from '../icon/icon';
import LayoutContext from '../context/layout-context';

export type SideModalProps = {
  /** The children passed to the SideModal component. */
  children: React.ReactNode;
  /** Callback passed to close the modal */
  handleCloseModal: () => void;
  /** Used to externally  manage modal state*/
  isModalOpen?: boolean;
};

const SideModal: FC<PropsWithChildren<SideModalProps>> = ({ children, handleCloseModal, isModalOpen = true }) => {
  const [isSelected, setIsSelected] = useState(false);

  const { shortCode } = useContext(LayoutContext);
  const lucidTheme = useTheme();
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_close_modal'],
    shortCode,
  });

  const handleClick = () => {
    setIsSelected(false);

    setTimeout(() => {
      handleCloseModal();
    }, 400);
  };

  useEffect(() => {
    if (!isModalOpen) {
      handleClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  useEffect(() => {
    if (isBrowser()) {
      // Account for layout shift when scrollbar is removed
      const windowWidth = window.innerWidth;
      const htmlWidth = window.getComputedStyle(document.children[0])?.width || '15';
      const diff = windowWidth - parseInt(htmlWidth);

      if (isSelected) document.body.setAttribute('style', `overflow: hidden; margin-right: ${diff}px`);
      else document.body.setAttribute('style', 'overflow: auto;');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);

  return (
    <ReactModal
      isOpen
      preventScroll
      ariaHideApp={false}
      onAfterOpen={() => setIsSelected(true)}
      onRequestClose={handleClick}
      shouldReturnFocusAfterClose={isModalOpen}
      style={{
        content: {
          padding: 0,
          left: 0,
          top: 0,
          border: 'none',
          background: lucidTheme.colors.coolGrey[100],
          height: '100%',
          transform: isSelected ? 'translateX(0%)' : 'translateX(100%)',
          transition: 'transform 200ms ease-out',
          display: 'flex',
          flexDirection: 'column-reverse',
          justifyContent: 'flex-end',
          position: 'unset',
          transitionDelay: isSelected ? '150ms' : '',
          maxWidth: window.matchMedia('(min-width: 1728px)').matches ? 'unset' : '500px',
          width: window.matchMedia('(min-width: 1728px)').matches ? '29%' : 'auto',
        },
        overlay: {
          zIndex: 9999999999,
          background: 'rgba(40, 44, 51, .7)',
          opacity: isSelected ? 1 : 0,
          transition: 'opacity 200ms ease-out',
          transitionDelay: !isSelected ? '150ms' : '',
          display: 'flex',
          justifyContent: 'flex-end',
        },
      }}
      testId="side-modal"
    >
      <Box css={{ overflow: 'auto' }}>{children}</Box>
      <Box
        css={theme => ({
          position: 'sticky',
          top: 0,
          background: 'inherit',
          textAlign: 'right',
          zIndex: theme.zIndices.z10,
          padding: `${theme.space.s32}px ${theme.space.s40}px 0`,
        })}
      >
        <Link
          asButton
          aria-label={withSmartling('mktg_close_modal')}
          css={theme => ({
            '&:focus-visible': { outline: `1px solid ${theme.colors.white}`, outlineOffset: '4px' },
          })}
          onClick={handleClick}
          variant="basic"
        >
          <Icon color="white" type="X" />
        </Link>
      </Box>
    </ReactModal>
  );
};

export { SideModal };
export default SideModal;
